import { useFormik } from "formik";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../../components/buttons/button";
import { FileUploadButton } from "../../../components/buttons/fileUploadButton";
import ComboBox from "../../../components/dropdown/comboBox";
import { EventImage } from "../../../components/event/eventImage";
import { DateInput } from "../../../components/inputs/dateInput";
import { TextInput } from "../../../components/inputs/textInput";
import { TimeInput } from "../../../components/inputs/timeInput";
import { TabNav } from "../../../components/tabNav";
import { ErrorText } from "../../../components/typography/errorText";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useFileUpload } from "../../../customeHooks/useFileUpload";
import { useGoogleMaps } from "../../../customeHooks/useGoogleMaps";
import { useEditEventMutation } from "../../../redux/features/event/event.api";
import { CreateEvent } from "../../../redux/features/event/event.types";
import { DEFAULT_EVENT_IMAGE_URL, MAX_SIZE_MB } from "../../../utils/constants";
import {
  formatDate,
  formatTime,
  parseDateStringToDate,
  parseTimeToDate,
} from "../../../utils/helpers";
import { EventDetailsSchema } from "../../../utils/validations";

export const EventDetails = ({
  finalPayload,
  setFinalPayload,
  setStep,
  mode,
  id = "",
  ticketsBought = false,
}: {
  finalPayload: CreateEvent;
  setFinalPayload: React.Dispatch<React.SetStateAction<CreateEvent>>;
  setStep: (step: number) => void;
  mode: "create" | "edit";
  id?: string;
  ticketsBought?: boolean;
}) => {
  const navigate = useNavigate();

  const {
    uploading,
    handleSingleFileUpload,
    error: imageUploadError,
    uploadedFile,
  } = useFileUpload();

  const {
    data: locations,
    isLoaded,
    loading: loadingLocations,
    handleSelect: handleLocationSelection,
    setAutoCompleteValue,
  } = useGoogleMaps();

  const [editEvent, { isLoading: editingEvent }] = useEditEventMutation();

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: finalPayload.name,
      description: finalPayload.description,
      location: finalPayload.location,
      startDate: finalPayload.startDate,
      endDate: finalPayload.endDate,
      startTime: finalPayload.startTime,
      endTime: finalPayload.endTime,
      image: finalPayload.image,
      type: finalPayload.type,
      status: finalPayload.status,
    },
    enableReinitialize: true,
    validationSchema: EventDetailsSchema,
    onSubmit: async (values) => {
      const payloadData = {
        ...finalPayload,
        ...values,
        image: values.image === "" ? DEFAULT_EVENT_IMAGE_URL : values.image,
      };
      if (
        mode === "create" ||
        (mode === "edit" && finalPayload.status === "draft")
      ) {
        setFinalPayload(payloadData);
        setStep(2);
      } else if (mode === "edit") {
        const resp = await editEvent({
          id: id,
          payload: {
            ...payloadData,
            startDate:
              parseDateStringToDate(payloadData.startDate)?.toISOString() ?? "",
            endDate:
              parseDateStringToDate(payloadData.endDate)?.toISOString() ?? "",
            tickets: undefined,
          },
        });

        if (resp.data?.status === "success") {
          toast.success("Event updated successfully");
          navigate(`/events/${resp.data.data.event.slug}/details`, {
            replace: true,
          });
        }
      }
    },
  });

  return (
    <>
      {(mode === "edit" && !ticketsBought) || mode === "create" ? (
        <div className="flex w-full flex-col gap-[2px]">
          <BgWrapper className="text-base font-bold text-grey-950">
            Event Details
          </BgWrapper>

          <BgWrapper className="">
            <TabNav
              navs={[
                {
                  title: "Physical Event",
                  to: "physical",
                  onClick: (selected) => {
                    setFieldValue("location", "");
                    setFieldValue("type", selected);
                  },
                },
                {
                  title: "Virtual Event",
                  to: "virtual",
                  onClick: (selected) => {
                    setFieldValue("location", "");
                    setFieldValue("type", selected);
                  },
                },
              ]}
              activeTab={values.type}
              variant="switcher"
              tabClassName=""
              className="col-span-full"
            />

            <form className="mt-4 grid grid-cols-2 items-start gap-4">
              <TextInput
                type="text"
                name="name"
                value={values.name}
                placeholder="Enter event name"
                onChange={handleChange}
                onBlur={handleBlur}
                hasTitle
                title="Event Name"
                errors={errors.name && touched.name ? errors.name : ""}
                autoFocus
                containerClassName="col-span-full"
              />

              <DateInput
                name="startDate"
                onBlur={handleBlur}
                placeholder="Select start date"
                errors={
                  errors.startDate && touched.startDate ? errors.startDate : ""
                }
                title="Start Date"
                date={parseDateStringToDate(values.startDate)}
                setDate={(date) => {
                  setFieldValue("startDate", formatDate(date));
                }}
              />

              <TimeInput
                name="startTime"
                onBlur={handleBlur}
                placeholder="Select start time"
                errors={
                  errors.startTime && touched.startTime ? errors.startTime : ""
                }
                title="Start Time"
                time={parseTimeToDate(values.startTime)}
                setDate={(time) => {
                  setFieldValue("startTime", formatTime(time));
                }}
              />

              <DateInput
                name="endDate"
                onBlur={handleBlur}
                placeholder="Select end date"
                errors={errors.endDate && touched.endDate ? errors.endDate : ""}
                title="End Date"
                date={parseDateStringToDate(values.endDate)}
                minDate={
                  values.startDate !== "" ? moment(values.startDate) : undefined
                }
                setDate={(date) => {
                  setFieldValue("endDate", formatDate(date));
                }}
              />

              <TimeInput
                name="endTime"
                onBlur={handleBlur}
                placeholder="Select end time"
                errors={errors.endTime && touched.endTime ? errors.endTime : ""}
                title="End Time"
                time={parseTimeToDate(values.endTime)}
                setDate={(time) => {
                  setFieldValue("endTime", formatTime(time));
                }}
              />

              {values.type === "physical" ? (
                <ComboBox
                  menuItems={locations.map((loc) => {
                    return {
                      name: loc.description,
                      value: {
                        name: loc.description,
                        id: loc.place_id,
                      },
                    };
                  })}
                  className="col-span-full"
                  loading={!isLoaded || loadingLocations}
                  onSelect={(val: { name: string; id: string }) => {
                    handleLocationSelection(
                      val.name,
                      val.id,
                      false,
                      (locData) => {
                        setFieldValue("location", val.name);
                      },
                    );
                  }}
                  onSearch={(search) => {
                    setAutoCompleteValue(search);
                  }}
                >
                  <TextInput
                    type="text"
                    name="location"
                    value={values.location}
                    placeholder={"Enter address"}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    hasTitle
                    title={"Address/Location"}
                    errors={
                      errors.location && touched.location ? errors.location : ""
                    }
                    containerClassName="col-span-full"
                  />
                </ComboBox>
              ) : (
                <TextInput
                  type="text"
                  name="location"
                  value={values.location}
                  placeholder={"Enter url"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title={"Meeting/Call Link"}
                  errors={
                    errors.location && touched.location ? errors.location : ""
                  }
                  containerClassName="col-span-full"
                />
              )}

              <TextInput
                type="textarea"
                name="description"
                value={values.description}
                placeholder="Enter description"
                onChange={handleChange}
                onBlur={handleBlur}
                hasTitle
                title="Description"
                errors={
                  errors.description && touched.description
                    ? errors.description
                    : ""
                }
                containerClassName="col-span-full"
                rows={1}
              />
            </form>
          </BgWrapper>
        </div>
      ) : undefined}

      <div
        className={`flex w-full flex-col gap-[2px] ${mode === "edit" && finalPayload.status !== "draft" ? "!order-first" : ""}`}
      >
        <BgWrapper className="text-base font-bold text-grey-950">
          Event Image
        </BgWrapper>

        <BgWrapper className="flex flex-col items-center gap-4 md:flex-row">
          <EventImage
            image={values.image}
            className="!h-[300px] md:!h-[200px]"
          />

          <div className="order-first h-fit flex-1 text-sm md:order-last">
            {imageUploadError && (
              <ErrorText text={imageUploadError} className="!test-sm" />
            )}

            {!imageUploadError && (
              <p className="line-clamp-1 max-w-full text-ellipsis">
                {uploadedFile
                  ? uploadedFile.name
                  : "Choose file to upload as event image"}
              </p>
            )}

            <p className="mt-[3px] text-grey-500">
              Recommended size 2,000 x 2,000 px, Max file size{" "}
              {MAX_SIZE_MB / (1024 * 1024)}MB
            </p>

            <FileUploadButton
              type="button"
              variation="plain-grey"
              className="mt-4 !max-w-fit"
              onUpload={(uploaded) => {
                handleSingleFileUpload(uploaded, (url) => {
                  setFieldValue("image", url);
                });
              }}
              text={values.image ? "Replace Image" : "Upload Image"}
              icon="upload"
              disabled={uploading}
              loading={uploading}
            />
          </div>
        </BgWrapper>
      </div>

      {mode === "edit" && ticketsBought ? (
        <div
          className={`flex w-full flex-col gap-[2px] ${mode === "edit" && finalPayload.status !== "draft" ? "!order-first" : ""}`}
        >
          <BgWrapper className="text-base font-bold text-grey-950">
            Event Details
          </BgWrapper>

          <BgWrapper className="">
            <TextInput
              type="textarea"
              name="description"
              value={values.description}
              placeholder="Enter description"
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title="Description"
              errors={
                errors.description && touched.description
                  ? errors.description
                  : ""
              }
              containerClassName="col-span-full"
              rows={1}
            />
          </BgWrapper>
        </div>
      ) : undefined}

      <BgWrapper>
        <Button
          text={
            mode === "create" ||
            (mode === "edit" && finalPayload.status === "draft")
              ? "Next"
              : "Save Changes"
          }
          type="button"
          onClick={() => {
            handleSubmit();
          }}
          disabled={uploading || editingEvent}
          loading={editingEvent}
        />
      </BgWrapper>
    </>
  );
};
